import {
	COLORS,
	Checkbox,
	MaterialIcons,
} from '@monthio/react-components'
import Typography from '@monthio/react-components/dist/Typography'
import {
	FC,
	HTMLAttributes,
	ReactNode,
	useCallback,
	useEffect,
	useState,
} from 'react'
import styled, { css } from 'styled-components'
import { Conditionally } from './conditionally'
import { ClickableContainer } from './ClickableContainer'
import { H2 } from '@monthio/design-system'

const Title = styled.div`
	flex: 1;
	overflow: hidden;

	> * {
		margin: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
`

const DescriptionWrapper = styled.div`
	display: flex;
	flex-direction: column;
`

const Logo = styled.div`
	> svg {
		height: 40px;
		width: 40px;
		aspect-ratio: 1 / 1;
		object-fit: cover;
		border-radius: 4px;
		box-shadow: 0 0 14px 0 #3540520d;

		${(props) => props.theme.breakpoints.up('md')} {
			height: 50px;
			width: 50px;
		}
	}
`

const CreditorLogo = styled.img`
	height: 40px;
	width: 40px;
	aspect-ratio: 1 / 1;
	object-fit: cover;
	border-radius: 4px;
	box-shadow: 0 0 14px 0 #3540520d;

	${(props) => props.theme.breakpoints.up('md')} {
		height: 50px;
		width: 50px;
	}
`

const HorizontalContainer = styled.div`
	display: flex;
	flex-direction: row;
	align-items: center;
	width: 100%;
	gap: 8px;
`

const ItemWithLogoContainer = styled(ClickableContainer)<{
	$isRounded?: boolean
	$hasShadow?: boolean
	$hasLogo?: boolean
	$isSelected?: boolean
	$hasBorder?: boolean
}>`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 8px;
	padding: 8px 8px 8px 12px;

	color: ${COLORS.TEXT_COLORS.DEFAULT};
	background: ${COLORS.GREY_SCALE.WHITE};

	${(p) =>
		p.$hasLogo &&
		css`
			padding: 8px;
		`}
	${(p) =>
		p.$hasBorder &&
		css`
			border: 1px solid #f1f2f4;
		`}
	${(p) =>
		p.$isRounded &&
		css`
			border-radius: 4px;
		`}
	${(p) =>
		p.$hasShadow &&
		css`
			border-radius: 4px;
		`}
	${(p) =>
		p.$isSelected &&
		css`
			border-radius: 4px;
			border: 1px solid ${COLORS.BRAND_COLORS.DARK_PERIWINKLE};
			background: ${COLORS.BRAND_COLORS.HIGHLIGHT_BLUE};
		`}
	${(p) =>
		p.$isClickable &&
		css`
			cursor: pointer;
		`}
`

const Wrapper = styled.div`
	.MuiFormControlLabel-root {
		margin-right: 0;
		padding: 0 0 0 8px;
	}
`

const Description = styled(Typography)`
	white-space: initial;
`

export interface ItemWithLogoProps extends HTMLAttributes<HTMLDivElement> {
	title: string
	description?: string | ReactNode
	financingType?: string
	logoSrc?: string
	logo?: JSX.Element
	isRounded?: boolean
	isSelected?: boolean
	hasBorder?: boolean
	onItemSelect?: (isSelected: boolean) => void
	footerContent?: ReactNode
}

const CreditorLogoWithFallback = ({
	logoSrc,
	logo,
}: Pick<ItemWithLogoProps, 'logoSrc' | 'logo'>): JSX.Element | null => {
	if (logoSrc) {
		return <CreditorLogo src={logoSrc} />
	}
	if (logo) {
		return <Logo>{logo}</Logo>
	}
	return null
}

export const ItemWithOptionalLogo: FC<ItemWithLogoProps> = ({
	title,
	description,
	financingType,
	logoSrc,
	logo,
	children,
	isRounded,
	hasBorder,
	isSelected,
	onItemSelect,
	footerContent,
	...domAttributes
}) => {
	const [isItemSelected, setItemIsSelected] = useState(isSelected ?? false)

	useEffect(() => {
		setItemIsSelected(isSelected ?? false)
	}, [isSelected])

	const handleClick = useCallback(
		(event) => {
			domAttributes.onClick?.(event)

			if (onItemSelect != undefined) {
				setItemIsSelected((prev) => {
					onItemSelect(!prev)
					return !prev
				})
			}
		},
		[domAttributes, onItemSelect],
	)

	return (
		<ItemWithLogoContainer
			$isClickable={!!domAttributes.onClick || !!onItemSelect}
			$hasLogo={!!logo}
			$isRounded={isRounded}
			$hasBorder={hasBorder}
			$isSelected={isItemSelected}
			onClick={handleClick}
			{...domAttributes}
		>
			<HorizontalContainer>
				<Conditionally condition={onItemSelect !== undefined}>
					<Wrapper>
						<Checkbox
							checked={isItemSelected}
							icon={<MaterialIcons.RadioButtonUnchecked />}
							checkedIcon={<MaterialIcons.RadioButtonChecked />}
							size="small"
							color="primary"
						/>
					</Wrapper>
				</Conditionally>
				<CreditorLogoWithFallback logoSrc={logoSrc} logo={logo} />
				<Title>
					<H2>{title}</H2>
					<DescriptionWrapper>
						<Conditionally condition={!!description}>
							<Description variant="caption">{description}</Description>
						</Conditionally>
						<Conditionally condition={!!financingType}>
							<Description variant="caption">{financingType}</Description>
						</Conditionally>
					</DescriptionWrapper>
				</Title>
				{children}
			</HorizontalContainer>
			{footerContent === undefined ? null : (
				<HorizontalContainer>{footerContent}</HorizontalContainer>
			)}
		</ItemWithLogoContainer>
	)
}
